import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  Animated,
  Dimensions,
  Easing,
  Image,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { usePrivy } from "@privy-io/react-auth";
import { useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import HousePickOptionChip from "./HousePickOptionChip";
import {
  HOUSE_PICK_CATEGORIES,
  getShuffledCategoryOrder,
  getRandomHandForCategory,
} from "./HousePickConstants";
import HousePickPromptInfoModal from "./HousePickPromptInfoModal";
import HousePickLoadingChips from "./HousePickLoadingChips";
import CopyButton from "./CopyButton";
import { openDirections } from "../../../utils/mapNavigation";
import { useUserVisitedRestaurants } from "../../../hooks/useUserVisitedRestaurants";
import { useFriendsVisitedRestaurants } from "../../../hooks/useFriendsVisitedRestaurants";
import { useHousePickDistanceUnit } from "../../../hooks/useHousePickDistanceUnit";
import { calculateDistance, formatDistance } from "../../../utils/googlePlaces";
import useHousePickStore from "./useHousePickStore";
import { useHousePickGooglePlaceMatches } from "./useHousePickGooglePlaceMatches";
import HousePickGooglePlacesRow from "./HousePickGooglePlacesRow";
import { useHousePickDisgoMatches } from "./useHousePickDisgoMatches";
import HousePickDisgoRow from "./HousePickDisgoRow";
import {
  HOUSE_PICK_PAGE_SIZE,
  buildHousePickPrompt,
} from "./housePickAiPrompt";
import { parseHousePickAiResponseText } from "./housePickParseAiResults";
import disgoChipIconGold from "../../../assets/disgo-chip-icon-gold.png";
import disgoChipIconSilver from "../../../assets/disgo-chip-icon-silver.png";

const CHIP_FLIGHT_DURATION_MS = 760;
const CHIP_EXIT_DURATION_MS = 420;
const CHIP_ENTER_DURATION_MS = 360;
const SUBMIT_CLEARANCE = 20;
const SUBMIT_LIFT = 28;

function measureInWindowAsync(node) {
  return new Promise((resolve) => {
    if (!node || typeof node.measureInWindow !== "function") {
      resolve(null);
      return;
    }
    node.measureInWindow((x, y, width, height) => {
      resolve({ x, y, width, height });
    });
  });
}

// ---------- Results sub-view (rendered inside the modal) ----------

function ResultsView({
  selectedOptions,
  userLatitude,
  userLongitude,
  onBack,
  onClose,
  onSelectRestaurant,
}) {
  const { unit: distanceUnit } = useHousePickDistanceUnit();
  const [showPromptInfoModal, setShowPromptInfoModal] = useState(false);
  const parsedResult = useHousePickStore((s) => s.parsedResult);
  const storeParsedResult = useHousePickStore((s) => s.setParsedResult);
  const [aiData, setAiData] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [isFetchingAI, setIsFetchingAI] = useState(
    () => !(Array.isArray(parsedResult) && parsedResult.length > 0)
  );

  const { getAccessToken } = usePrivy();
  const callOpenAIAction = useAction(api.openai.callOpenAIAction);

  const userVisitedRestaurants = useUserVisitedRestaurants();
  const friendsVisitedRestaurants = useFriendsVisitedRestaurants();

  const [resultPool, setResultPool] = useState([]);
  const [resultPage, setResultPage] = useState(0);
  const [previouslyReturnedRestaurants, setPreviouslyReturnedRestaurants] = useState([]);
  const requestGenRef = useRef(0);
  const previouslyReturnedRef = useRef([]);
  const contextRef = useRef({});
  contextRef.current = {
    selectedOptions,
    userLatitude,
    userLongitude,
    userVisitedRestaurants,
    friendsVisitedRestaurants,
    getAccessToken,
    callOpenAIAction,
    storeParsedResult,
  };

  const buildPrompt = useCallback(
    () =>
      buildHousePickPrompt({
        selectedOptions,
        userLatitude,
        userLongitude,
        userVisitedRestaurants,
        friendsVisitedRestaurants,
        previouslyReturnedRestaurants,
      }),
    [
      selectedOptions,
      userLatitude,
      userLongitude,
      userVisitedRestaurants,
      friendsVisitedRestaurants,
      previouslyReturnedRestaurants,
    ]
  );

  const rememberPoolNames = useCallback((pool) => {
    if (!Array.isArray(pool) || pool.length === 0) return;
    const seen = new Set(
      previouslyReturnedRef.current.map((name) => name.toLowerCase())
    );
    const merged = [...previouslyReturnedRef.current];
    for (const row of pool) {
      const name = typeof row?.name === "string" ? row.name.trim() : "";
      if (!name) continue;
      const normalized = name.toLowerCase();
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      merged.push(name);
    }
    previouslyReturnedRef.current = merged;
    setPreviouslyReturnedRestaurants(merged);
  }, []);

  const fetchAI = useCallback(async () => {
    const gen = ++requestGenRef.current;
    const ctx = contextRef.current;
    const excludeNames = previouslyReturnedRef.current;
    setIsFetchingAI(true);
    setAiError(null);
    setAiData(null);
    setResultPool([]);
    setResultPage(0);
    ctx.storeParsedResult(null);

    try {
      const accessToken = await ctx.getAccessToken();
      if (!accessToken) throw new Error("Not authenticated");
      if (gen !== requestGenRef.current) return;

      const prompt = buildHousePickPrompt({
        selectedOptions: ctx.selectedOptions,
        userLatitude: ctx.userLatitude,
        userLongitude: ctx.userLongitude,
        userVisitedRestaurants: ctx.userVisitedRestaurants,
        friendsVisitedRestaurants: ctx.friendsVisitedRestaurants,
        previouslyReturnedRestaurants: excludeNames,
      });
      const response = await ctx.callOpenAIAction({ prompt, accessToken });
      if (gen !== requestGenRef.current) return;

      setAiData(response);
      const pool = parseHousePickAiResponseText(response);
      if (!pool.length) throw new Error("No restaurants returned");
      rememberPoolNames(pool);
      setResultPool(pool);
      setResultPage(0);
      ctx.storeParsedResult(pool.slice(0, HOUSE_PICK_PAGE_SIZE));
      setIsFetchingAI(false);
    } catch (error) {
      if (gen !== requestGenRef.current) return;
      console.error("Error fetching AI response:", error);
      setAiError(error.message || "Failed to get AI response");
      setResultPool([]);
      setResultPage(0);
      ctx.storeParsedResult(null);
      setIsFetchingAI(false);
    }
  }, [rememberPoolNames]);

  useEffect(() => {
    if (Array.isArray(parsedResult) && parsedResult.length > 0) return undefined;
    previouslyReturnedRef.current = [];
    setPreviouslyReturnedRestaurants([]);
    fetchAI();
    return () => {
      requestGenRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount
  }, []);

  const refetchAiBatch = useCallback(() => {
    fetchAI();
  }, [fetchAI]);

  const runItBack = useCallback(() => {
    const nextPage = resultPage + 1;
    const nextStart = nextPage * HOUSE_PICK_PAGE_SIZE;
    if (resultPool.length > nextStart) {
      setResultPage(nextPage);
      storeParsedResult(resultPool.slice(nextStart, nextStart + HOUSE_PICK_PAGE_SIZE));
      return;
    }
    fetchAI();
  }, [resultPage, resultPool, storeParsedResult, fetchAI]);

  const distanceLabels = useMemo(() => {
    if (!Array.isArray(parsedResult)) return [];
    return parsedResult.map((r) => {
      const km = calculateDistance(
        userLatitude,
        userLongitude,
        r?.coordinates?.latitude,
        r?.coordinates?.longitude
      );
      return formatDistance(km, distanceUnit) || null;
    });
  }, [parsedResult, userLatitude, userLongitude, distanceUnit]);

  const googlePlaceMatches = useHousePickGooglePlaceMatches(
    Array.isArray(parsedResult) && parsedResult.length > 0 && !aiError && !isFetchingAI
      ? parsedResult
      : null,
    userLatitude,
    userLongitude
  );
  const disgoMatches = useHousePickDisgoMatches(googlePlaceMatches);

  return (
    <View style={styles.fullScreenContainer}>
      <View style={styles.resultsHeader}>
        <View style={styles.resultsHeaderTopRow}>
          <TouchableOpacity onPress={onBack} hitSlop={8} style={styles.resultsBackButton}>
            <Ionicons name="chevron-back" size={28} color="#4d2c1d" />
          </TouchableOpacity>
          <Text numberOfLines={1} style={styles.resultsHeaderTitle}>
            Your Draw
          </Text>
          <View style={styles.resultsHeaderSpacer} />
        </View>
        <View style={styles.resultsHeaderHelpRow}>
          <TouchableOpacity
            onPress={() => setShowPromptInfoModal(true)}
            hitSlop={8}
            style={styles.resultsHelpButton}
          >
            <Ionicons name="help-circle-outline" size={30} color="#6B4C3B" />
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.resultsContent}>
        <View style={styles.resultsSection}>
          {isFetchingAI && (
            <View style={styles.loadingCard}>
              <ActivityIndicator size="small" color="#6B4C3B" style={{ marginBottom: 10 }} />
              <Text style={styles.loadingTitle}>Cooking up your results...</Text>
              <Text style={styles.loadingSubtitle}>Matching your vibe with nearby spots</Text>
            </View>
          )}

          {aiError && !isFetchingAI && (
            <View style={styles.errorContainer}>
              <Ionicons name="alert-circle-outline" size={44} color="#9C8A80" style={{ marginBottom: 16 }} />
              <Text style={styles.errorTitle}>Something went wrong</Text>
              <Text style={styles.errorMessage}>Please try again in a bit or contact support.</Text>
              <TouchableOpacity
                onPress={refetchAiBatch}
                style={[styles.retryButton, { marginTop: 24 }]}
                activeOpacity={0.85}
              >
                <Text style={styles.retryButtonText}>Try again</Text>
              </TouchableOpacity>
            </View>
          )}

          {Array.isArray(parsedResult) && !aiError && !isFetchingAI &&
            parsedResult.map((result, index) => (
              <View key={index} style={styles.resultCard}>
                <View style={styles.resultTitleRow}>
                  <Text numberOfLines={1} style={styles.resultTitle}>
                    {result?.name}
                  </Text>
                  <View style={styles.resultBadgeRow}>
                    <View style={styles.resultBadge}>
                      <Ionicons name="sparkles" size={12} color="#6B4C3B" />
                      <Text style={styles.resultBadgeText}>House Pick!</Text>
                    </View>
                    {!!distanceLabels[index] && (
                      <View style={styles.distancePill}>
                        <Ionicons name="navigate-outline" size={11} color="#7D695F" />
                        <Text style={styles.distancePillText}>{distanceLabels[index]}</Text>
                      </View>
                    )}
                  </View>
                </View>
                <Text numberOfLines={2} style={styles.resultDescription}>
                  {result?.description}
                </Text>
                {!!result?.address && (
                  <View style={styles.addressRow}>
                    <Ionicons name="location-outline" size={14} color="#7D695F" />
                    <Text numberOfLines={1} style={styles.addressText}>{result.address}</Text>
                    <View style={styles.addressActions}>
                      <CopyButton textToCopy={result.address} />
                      <TouchableOpacity
                        onPress={async () => { await openDirections({ address: result.address }); }}
                        activeOpacity={0.85}
                        accessibilityLabel="Open directions in maps"
                        style={styles.directionsButton}
                      >
                        <Ionicons name="navigate-outline" size={14} color="#FFFFFF" />
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
                <HousePickGooglePlacesRow matchState={googlePlaceMatches[index]} compact />
                <HousePickDisgoRow
                  aiResult={result}
                  googleMatch={googlePlaceMatches[index]}
                  disgoMatch={disgoMatches[index]}
                  onPressRestaurant={onSelectRestaurant}
                  compact
                />
              </View>
            ))}
        </View>
      </View>

      <View style={styles.bottomActions}>
        {!aiError && !isFetchingAI && (
          <>
            <TouchableOpacity
              onPress={runItBack}
              style={styles.retryButton}
              activeOpacity={0.85}
            >
              <Text style={styles.retryButtonText}>Run it back!</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={onClose} style={styles.closeBodyButton} activeOpacity={0.85}>
              <Ionicons name="close" size={22} color="#4d2c1d" />
              <Text style={styles.closeBodyButtonText}>Close</Text>
            </TouchableOpacity>
          </>
        )}
      </View>

      <HousePickPromptInfoModal
        isVisible={showPromptInfoModal}
        onClose={() => setShowPromptInfoModal(false)}
        getPromptText={buildPrompt}
        rawResponse={aiData}
        onRetry={refetchAiBatch}
      />

      <HousePickLoadingChips active={isFetchingAI} />
    </View>
  );
}

// ---------- Main bottom sheet component ----------

const HousePickBottomSheet = forwardRef(function HousePickBottomSheet(
  { userLatitude, userLongitude, mapBounds, onSelectRestaurant, embedded = false, onRequestClose },
  ref
) {
  const storeParsedResult = useHousePickStore((s) => s.parsedResult);
  const setStoreParsedResult = useHousePickStore((s) => s.setParsedResult);
  const storeSelectedOptions = useHousePickStore((s) => s.selectedOptions);
  const setStoreSelectedOptions = useHousePickStore((s) => s.setSelectedOptions);
  const [isVisible, setIsVisible] = useState(false);
  const [showResultsView, setShowResultsView] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(["Within 15 miles"]);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showPickDetailModal, setShowPickDetailModal] = useState(false);
  const [selectedPickDetail, setSelectedPickDetail] = useState(null);
  const [customPickInput, setCustomPickInput] = useState("");
  const [categoryState, setCategoryState] = useState(() => {
    const order = getShuffledCategoryOrder();
    return {
      categoryOrder: order,
      activeCategoryIndex: 0,
      currentHand: getRandomHandForCategory(HOUSE_PICK_CATEGORIES[order[0]]),
    };
  });
  const [animatingOptions, setAnimatingOptions] = useState([]);
  const [exitingOptions, setExitingOptions] = useState([]);
  const [isEnteringSet, setIsEnteringSet] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [flyingChip, setFlyingChip] = useState(null);

  const insets = { top: 0, bottom: 0, left: 0, right: 0 };

  const picksButtonRef = useRef(null);
  const optionRefs = useRef({});
  const flightProgress = useRef(new Animated.Value(0)).current;
  const exitProgress = useRef(new Animated.Value(0)).current;
  const enterProgress = useRef(new Animated.Value(1)).current;
  const flightTimeoutRef = useRef(null);

  const activeOptions = categoryState.currentHand;

  useEffect(() => {
    return () => {
      if (flightTimeoutRef.current) {
        clearTimeout(flightTimeoutRef.current);
        flightTimeoutRef.current = null;
      }
      flightProgress.stopAnimation();
      exitProgress.stopAnimation();
      enterProgress.stopAnimation();
    };
  }, [enterProgress, exitProgress, flightProgress]);

  const resetTransientState = () => {
    if (flightTimeoutRef.current) {
      clearTimeout(flightTimeoutRef.current);
      flightTimeoutRef.current = null;
    }
    flightProgress.stopAnimation();
    flightProgress.setValue(0);
    setSelectedOptions(["Within 15 miles"]);
    setFlyingChip(null);
    setAnimatingOptions([]);
    setShowCustomModal(false);
    setShowResultsView(false);
    setCustomPickInput("");
    setCategoryState(() => {
      const order = getShuffledCategoryOrder();
      return {
        categoryOrder: order,
        activeCategoryIndex: 0,
        currentHand: getRandomHandForCategory(HOUSE_PICK_CATEGORIES[order[0]]),
      };
    });
    setExitingOptions([]);
    setIsEnteringSet(false);
    setIsSubmitting(false);
    exitProgress.stopAnimation();
    exitProgress.setValue(0);
    enterProgress.stopAnimation();
    enterProgress.setValue(1);
  };

  const closeHousePick = (opts) => {
    if (!opts?.preserveState) {
      resetTransientState();
    }
    if (embedded) {
      onRequestClose?.();
    } else {
      setIsVisible(false);
    }
  };

  const dismissPreservingState = () => {
    if (embedded) return;
    closeHousePick({ preserveState: true });
  };

  const openHousePick = useCallback(() => {
    const hasResults = Array.isArray(storeParsedResult) && storeParsedResult.length > 0;
    if (hasResults) {
      if (Array.isArray(storeSelectedOptions) && storeSelectedOptions.length > 0) {
        setSelectedOptions(storeSelectedOptions);
      }
      setShowResultsView(true);
    }
    if (!embedded) {
      setIsVisible(true);
    }
  }, [storeParsedResult, storeSelectedOptions, embedded]);

  useEffect(() => {
    if (!embedded) return;
    const hasResults = Array.isArray(storeParsedResult) && storeParsedResult.length > 0;
    if (hasResults) {
      if (Array.isArray(storeSelectedOptions) && storeSelectedOptions.length > 0) {
        setSelectedOptions(storeSelectedOptions);
      }
      setShowResultsView(true);
    }
  }, [embedded, storeParsedResult, storeSelectedOptions]);

  useImperativeHandle(
    ref,
    () => ({
      snapToIndex: embedded ? () => {} : openHousePick,
      expand: embedded ? () => {} : openHousePick,
      collapse: closeHousePick,
      close: closeHousePick,
      forceClose: closeHousePick,
      dismissPreservingState,
    }),
    [embedded, openHousePick]
  );

  const removePick = (pickToRemove) => {
    setSelectedOptions((prev) => prev.filter((item) => item !== pickToRemove));
  };

  const startRemainingExit = (pickedOption, optionSet) => {
    if (isSubmitting) return;

    const remainingOptions = optionSet.filter(
      (option) =>
        option !== pickedOption &&
        !selectedOptions.includes(option) &&
        !animatingOptions.includes(option)
    );

    if (remainingOptions.length === 0) return;

    setIsSubmitting(true);
    setExitingOptions(remainingOptions);
    exitProgress.stopAnimation();
    exitProgress.setValue(0);

    const { categoryOrder, activeCategoryIndex } = categoryState;
    const nextIndex = (activeCategoryIndex + 1) % categoryOrder.length;
    const nextCategory = HOUSE_PICK_CATEGORIES[categoryOrder[nextIndex]];

    Animated.timing(exitProgress, {
      toValue: 1,
      duration: CHIP_EXIT_DURATION_MS,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: false,
    }).start(() => {
      setExitingOptions([]);
      setCategoryState((prev) => ({
        ...prev,
        activeCategoryIndex: nextIndex,
        currentHand: getRandomHandForCategory(nextCategory),
      }));
      setIsEnteringSet(true);
      enterProgress.stopAnimation();
      enterProgress.setValue(0);
      Animated.timing(enterProgress, {
        toValue: 1,
        duration: CHIP_ENTER_DURATION_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start(() => {
        setIsEnteringSet(false);
        setIsSubmitting(false);
      });
    });
  };

  const animateOptionToPicks = async (option) => {
    if (flyingChip) return;

    const sourceNode = optionRefs.current[option];
    const targetNode = picksButtonRef.current;

    if (!sourceNode || !targetNode) {
      setSelectedOptions((prev) =>
        prev.includes(option) ? prev : [...prev, option]
      );
      startRemainingExit(option, activeOptions);
      return;
    }

    const [sourceRect, targetRect] = await Promise.all([
      measureInWindowAsync(sourceNode),
      measureInWindowAsync(targetNode),
    ]);

    if (!sourceRect || !targetRect) {
      setSelectedOptions((prev) =>
        prev.includes(option) ? prev : [...prev, option]
      );
      startRemainingExit(option, activeOptions);
      return;
    }

    const startX = sourceRect.x + sourceRect.width / 2;
    const startY = sourceRect.y + sourceRect.height / 2;
    const endX = targetRect.x + targetRect.width / 2;
    const endY = targetRect.y + targetRect.height / 2;
    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const arcLift = Math.max(96, Math.abs(deltaY) * 0.38);
    const flyRotateY = deltaX >= 0 ? "26deg" : "-26deg";
    const flyRotateZ = deltaX >= 0 ? "18deg" : "-18deg";

    setAnimatingOptions((prev) =>
      prev.includes(option) ? prev : [...prev, option]
    );
    setFlyingChip({
      label: option,
      width: sourceRect.width,
      left: startX - sourceRect.width / 2,
      top: startY - sourceRect.height / 2,
      deltaX,
      deltaY,
      arcLift,
      flyRotateY,
      flyRotateZ,
    });

    flightProgress.stopAnimation();
    flightProgress.setValue(0);

    let finalized = false;
    const finalizeFlight = () => {
      if (finalized) return;
      finalized = true;

      if (flightTimeoutRef.current) {
        clearTimeout(flightTimeoutRef.current);
        flightTimeoutRef.current = null;
      }

      setFlyingChip(null);
      setAnimatingOptions((prev) => prev.filter((item) => item !== option));
      setSelectedOptions((prev) =>
        prev.includes(option) ? prev : [...prev, option]
      );
      startRemainingExit(option, activeOptions);
    };

    flightTimeoutRef.current = setTimeout(
      finalizeFlight,
      CHIP_FLIGHT_DURATION_MS + 180
    );

    Animated.timing(flightProgress, {
      toValue: 1,
      duration: CHIP_FLIGHT_DURATION_MS,
      easing: Easing.inOut(Easing.cubic),
      useNativeDriver: false,
    }).start(finalizeFlight);
  };

  const addCustomPick = () => {
    const customPick = customPickInput.trim();
    if (!customPick) return;

    const exists = selectedOptions.some(
      (item) => item.toLowerCase() === customPick.toLowerCase()
    );
    if (!exists) {
      setSelectedOptions((prev) => [...prev, customPick]);
      startRemainingExit("", activeOptions);
    }

    setCustomPickInput("");
    setShowCustomModal(false);
  };

  const handleSubmit = () => {
    if (isSubmitting) return;
    setShowCustomModal(false);
    setShowPickDetailModal(false);
    setStoreSelectedOptions(selectedOptions);
    setStoreParsedResult(null);
    setShowResultsView(true);
  };

  const handleResultsBack = () => {
    setShowResultsView(false);
  };

  if (!embedded && !isVisible) return null;

  const panelPaddingTop = embedded ? 4 : insets.top + 8;

  const pickModalBody = showResultsView ? (
    <ResultsView
      selectedOptions={selectedOptions}
      userLatitude={userLatitude}
      userLongitude={userLongitude}
      onSelectRestaurant={onSelectRestaurant}
      onBack={handleResultsBack}
      onClose={() => closeHousePick()}
    />
  ) : (
    <View style={styles.fullScreenContainer}>
      <View style={[styles.panelHeader, { paddingTop: panelPaddingTop }]}>
        <TouchableOpacity onPress={closeHousePick} style={styles.closeButton}>
          <Ionicons name="close" size={24} color="#666" />
        </TouchableOpacity>
        <Text style={styles.panelHeaderTitle} numberOfLines={1}>
          House Pick
        </Text>
        <View style={styles.panelHeaderSpacer} />
      </View>

      <ScrollView
        style={styles.scroll}
        scrollEnabled={!!embedded}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: insets.bottom + SUBMIT_CLEARANCE },
        ]}
      >
            <Text style={styles.subheading}>What&apos;s today&apos;s vibe?</Text>
            <View style={styles.spacer} />

            <View style={[styles.bottomContent, { paddingBottom: insets.bottom + SUBMIT_LIFT }]}>
              <View ref={picksButtonRef} collapsable={false} style={styles.myPicksSectionContainer}>
                <View style={styles.myPicksSection}>
                  <Text style={styles.myPicksLabel}>My Picks ({selectedOptions.length})</Text>
                  {selectedOptions.length > 0 ? (
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      contentContainerStyle={styles.myPicksScrollContent}
                      style={styles.myPicksScroll}
                    >
                      {selectedOptions.map((pick) => (
                        <View key={pick} style={styles.myPickChipContainer}>
                          <View style={styles.myPickChipWrapper}>
                            <HousePickOptionChip
                              label={pick}
                              isSelected
                              iconSource={disgoChipIconGold}
                              onPress={() => {
                                setSelectedPickDetail(pick);
                                setShowPickDetailModal(true);
                              }}
                              containerStyle={styles.myPickChip}
                              labelStyle={styles.myPickChipLabel}
                              labelNumberOfLines={1}
                            />
                            <Pressable
                              style={styles.myPickRemoveButton}
                              onPress={() => removePick(pick)}
                            >
                              <Ionicons name="close" size={16} color="#FFFFFF" />
                            </Pressable>
                          </View>
                        </View>
                      ))}
                    </ScrollView>
                  ) : (
                    <View style={styles.emptyMyPicksContainer}>
                      <Text style={styles.emptyMyPicksText}>
                        Tap options below to add picks.
                      </Text>
                    </View>
                  )}
                </View>
              </View>

              <View style={styles.optionsWrap}>
                {activeOptions.map((option) => {
                  const isPickedOrAnimating =
                    selectedOptions.includes(option) ||
                    animatingOptions.includes(option);
                  const isExiting = exitingOptions.includes(option);

                  if (isPickedOrAnimating) {
                    return (
                      <View key={option} style={styles.optionPlaceholder}>
                        <View style={styles.optionPlaceholderCard} />
                        <View style={styles.optionPlaceholderLabelSpace} />
                      </View>
                    );
                  }

                  const exitAnimatedStyle = isExiting
                    ? {
                        transform: [
                          {
                            translateX: exitProgress.interpolate({
                              inputRange: [0, 1],
                              outputRange: [0, Math.max(360, Dimensions.get("window").width * 1.1)],
                            }),
                          },
                        ],
                        opacity: exitProgress.interpolate({
                          inputRange: [0, 0.82, 1],
                          outputRange: [1, 1, 0],
                        }),
                      }
                    : null;

                  const enterAnimatedStyle =
                    !isExiting && isEnteringSet
                      ? {
                          transform: [
                            {
                              translateX: enterProgress.interpolate({
                                inputRange: [0, 1],
                                outputRange: [-Math.max(360, Dimensions.get("window").width * 1.05), 0],
                              }),
                            },
                          ],
                          opacity: enterProgress.interpolate({
                            inputRange: [0, 1],
                            outputRange: [0, 1],
                          }),
                        }
                      : null;

                  return (
                    <Animated.View
                      key={option}
                      style={[styles.optionAnimatedItem, enterAnimatedStyle, exitAnimatedStyle]}
                    >
                      <HousePickOptionChip
                        ref={(node) => {
                          optionRefs.current[option] = node;
                        }}
                        onPress={isSubmitting ? undefined : () => animateOptionToPicks(option)}
                        label={option}
                        isSelected={false}
                        containerStyle={styles.optionInAnimatedItem}
                      />
                    </Animated.View>
                  );
                })}
              </View>

              <Pressable style={styles.customButton} onPress={() => setShowCustomModal(true)}>
                <Text style={styles.customButtonText}>Custom</Text>
              </Pressable>

              <Pressable
                style={[styles.submitButton, isSubmitting && styles.submitButtonDisabled]}
                onPress={handleSubmit}
                disabled={isSubmitting}
              >
                <Text style={styles.submitButtonText}>House, Pick!</Text>
              </Pressable>
            </View>
          </ScrollView>

          {flyingChip ? (
            <View pointerEvents="none" style={styles.flightOverlay}>
              <Animated.View
                pointerEvents="none"
                style={[
                  styles.flyingChipLayer,
                  {
                    left: flyingChip.left,
                    top: flyingChip.top,
                    width: flyingChip.width,
                    transform: [
                      { perspective: 1200 },
                      {
                        translateX: flightProgress.interpolate({
                          inputRange: [0, 1],
                          outputRange: [0, flyingChip.deltaX],
                        }),
                      },
                      {
                        translateY: flightProgress.interpolate({
                          inputRange: [0, 0.62, 1],
                          outputRange: [0, -flyingChip.arcLift, flyingChip.deltaY],
                        }),
                      },
                      {
                        rotateX: flightProgress.interpolate({
                          inputRange: [0, 1],
                          outputRange: ["0deg", "64deg"],
                        }),
                      },
                      {
                        rotateY: flightProgress.interpolate({
                          inputRange: [0, 1],
                          outputRange: ["0deg", flyingChip.flyRotateY],
                        }),
                      },
                      {
                        rotateZ: flightProgress.interpolate({
                          inputRange: [0, 1],
                          outputRange: ["0deg", flyingChip.flyRotateZ],
                        }),
                      },
                      {
                        scale: flightProgress.interpolate({
                          inputRange: [0, 1],
                          outputRange: [1, 0.72],
                        }),
                      },
                    ],
                    opacity: flightProgress.interpolate({
                      inputRange: [0, 0.84, 1],
                      outputRange: [1, 1, 0.2],
                    }),
                  },
                ]}
              >
                <HousePickOptionChip
                  label={flyingChip.label}
                  isSelected={false}
                  containerStyle={styles.flyingChip}
                />
              </Animated.View>
            </View>
          ) : null}

          {showPickDetailModal ? (
            <View style={styles.overlay}>
              <Pressable style={styles.backdrop} onPress={() => setShowPickDetailModal(false)} />
              <View style={styles.modalCard}>
                <View style={styles.pickDetailChipContainer}>
                  <HousePickOptionChip
                    label={selectedPickDetail}
                    isSelected
                    iconSource={disgoChipIconGold}
                    containerStyle={styles.pickDetailChip}
                    labelStyle={styles.pickDetailChipLabel}
                    labelNumberOfLines={2}
                  />
                </View>
                <Pressable
                  style={styles.closeModalButton}
                  onPress={() => setShowPickDetailModal(false)}
                >
                  <Text style={styles.closeModalButtonText}>Close</Text>
                </Pressable>
              </View>
            </View>
          ) : null}

          {showCustomModal ? (
            <View style={styles.overlay}>
              <Pressable style={styles.backdrop} onPress={() => setShowCustomModal(false)} />
              <View style={styles.modalCard}>
                <Text style={styles.modalTitle}>Add Custom Pick</Text>
                <View style={styles.customModalChipContainer}>
                  <Image
                    source={disgoChipIconSilver}
                    style={styles.customModalChipImage}
                    resizeMode="contain"
                  />
                </View>

                <TextInput
                  value={customPickInput}
                  onChangeText={setCustomPickInput}
                  placeholder="Type your pick..."
                  placeholderTextColor="#9C8A80"
                  style={styles.customInput}
                  autoFocus
                  returnKeyType="done"
                  onSubmitEditing={addCustomPick}
                />

                <View style={styles.customModalActions}>
                  <Pressable
                    style={[styles.customActionButton, styles.customCancelButton]}
                    onPress={() => {
                      setCustomPickInput("");
                      setShowCustomModal(false);
                    }}
                  >
                    <Text style={styles.customCancelButtonText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.customActionButton, styles.customAddButton]}
                    onPress={addCustomPick}
                  >
                    <Text style={styles.customAddButtonText}>Add</Text>
                  </Pressable>
                </View>
              </View>
            </View>
          ) : null}
        </View>
  );

  if (embedded) {
    return <View style={styles.embeddedHousePickRoot}>{pickModalBody}</View>;
  }

  return (
    <Modal
      visible={isVisible}
      animationType="slide"
      presentationStyle="fullScreen"
      statusBarTranslucent
      onRequestClose={closeHousePick}
    >
      {pickModalBody}
    </Modal>
  );
});

const styles = StyleSheet.create({
  embeddedHousePickRoot: {
    flex: 1,
    minHeight: 0,
    backgroundColor: "#F9F5F3",
  },
  // ── Shared / Pick view ──
  fullScreenContainer: {
    flex: 1,
    backgroundColor: "#F9F5F3",
  },
  panelHeader: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: 44,
    paddingHorizontal: 8,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    backgroundColor: "#F9F5F3",
  },
  closeButton: {
    width: 44,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
  },
  panelHeaderTitle: {
    flex: 1,
    fontSize: 28,
    color: "#4d2c1d",
    fontFamily: "AbhayaLibre_700Bold",
    textAlign: "center",
  },
  panelHeaderSpacer: {
    width: 44,
  },
  scroll: {
    flex: 1,
    backgroundColor: "#F9F5F3",
  },
  scrollContent: {
    flexGrow: 1,
    backgroundColor: "#F9F5F3",
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  subheading: {
    fontSize: 28,
    color: "#6B4C3B",
    fontFamily: "AbhayaLibre_700Bold",
    textAlign: "center",
  },
  spacer: {
    height: 2,
  },
  bottomContent: {},
  myPicksSectionContainer: {
    minHeight: 0,
    marginBottom: 6,
  },
  myPicksSection: {
    marginBottom: 6,
    minHeight: 120,
  },
  myPicksLabel: {
    fontSize: 18,
    color: "#6B4C3B",
    fontFamily: "AbhayaLibre_700Bold",
    marginBottom: 6,
    paddingHorizontal: 4,
  },
  emptyMyPicksContainer: {
    minHeight: 80,
    justifyContent: "center",
    alignItems: "center",
  },
  emptyMyPicksText: {
    fontSize: 14,
    color: "#9C8A80",
    fontFamily: "AbhayaLibre_400Regular",
    textAlign: "center",
    paddingVertical: 12,
    paddingHorizontal: 4,
  },
  myPicksScroll: {
    maxHeight: 120,
  },
  myPicksScrollContent: {
    paddingRight: 16,
    gap: 12,
  },
  myPickChipContainer: {
    width: 100,
    paddingTop: 4,
    paddingRight: 4,
  },
  myPickChipWrapper: {
    position: "relative",
    width: "100%",
  },
  myPickChip: {
    width: "100%",
    marginBottom: 0,
  },
  myPickChipLabel: {
    minHeight: 0,
    marginTop: 3,
    fontSize: 14,
    lineHeight: 16,
  },
  myPickRemoveButton: {
    position: "absolute",
    top: 0,
    right: 0,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "#DC2626",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
  },
  optionsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  optionAnimatedItem: {
    width: "48.5%",
  },
  optionInAnimatedItem: {
    width: "100%",
  },
  optionPlaceholder: {
    width: "48.5%",
    marginBottom: 12,
    alignItems: "center",
  },
  optionPlaceholderCard: {
    width: "70%",
    aspectRatio: 1,
  },
  optionPlaceholderLabelSpace: {
    marginTop: 4,
    height: 36,
  },
  customButton: {
    width: "100%",
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "#6B4C3B",
    backgroundColor: "#FFFFFF",
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 10,
  },
  customButtonText: {
    color: "#6B4C3B",
    fontSize: 16,
    fontFamily: "AbhayaLibre_700Bold",
    textAlign: "center",
  },
  submitButton: {
    marginTop: 10,
    borderRadius: 12,
    backgroundColor: "#6B4C3B",
    paddingVertical: 16,
    alignItems: "center",
  },
  submitButtonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: "#FFFFFF",
    fontSize: 24,
    fontFamily: "AbhayaLibre_700Bold",
  },
  flightOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1000,
  },
  flyingChipLayer: {
    position: "absolute",
    zIndex: 1000,
  },
  flyingChip: {
    width: "100%",
    marginBottom: 0,
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1200,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.35)",
  },
  modalCard: {
    width: "100%",
    maxWidth: 360,
    maxHeight: "82%",
    borderRadius: 16,
    backgroundColor: "#F9F5F3",
    borderWidth: 2,
    borderColor: "#6B4C3B",
    padding: 16,
  },
  modalTitle: {
    fontSize: 24,
    color: "#000000",
    fontFamily: "AbhayaLibre_700Bold",
    textAlign: "center",
    marginBottom: 12,
  },
  closeModalButton: {
    marginTop: 12,
    borderRadius: 10,
    backgroundColor: "#6B4C3B",
    alignItems: "center",
    paddingVertical: 10,
  },
  closeModalButtonText: {
    fontSize: 18,
    color: "#FFFFFF",
    fontFamily: "AbhayaLibre_700Bold",
  },
  pickDetailChipContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginVertical: 20,
    paddingHorizontal: 16,
  },
  pickDetailChip: {
    width: 200,
    marginBottom: 0,
  },
  pickDetailChipLabel: {
    minHeight: 0,
    marginTop: 4,
    fontSize: 20,
    lineHeight: 24,
  },
  customModalChipContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  customModalChipImage: {
    width: 112,
    height: 112,
  },
  customInput: {
    borderWidth: 1.5,
    borderColor: "#C9B2A4",
    backgroundColor: "#FFFFFF",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: "#2A1A14",
    fontFamily: "AbhayaLibre_400Regular",
    marginBottom: 12,
  },
  customModalActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 10,
  },
  customActionButton: {
    flex: 1,
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: "center",
  },
  customCancelButton: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1.5,
    borderColor: "#6B4C3B",
  },
  customCancelButtonText: {
    color: "#6B4C3B",
    fontSize: 16,
    fontFamily: "AbhayaLibre_700Bold",
  },
  customAddButton: {
    backgroundColor: "#6B4C3B",
  },
  customAddButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontFamily: "AbhayaLibre_700Bold",
  },

  // ── Results view ──
  resultsHeader: {
    paddingHorizontal: 8,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    alignItems: "stretch",
    justifyContent: "center",
    backgroundColor: "#F9F5F3",
  },
  resultsHeaderTopRow: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: 56,
  },
  resultsBackButton: {
    width: 44,
    height: 44,
    alignItems: "flex-start",
    justifyContent: "center",
  },
  resultsHeaderTitle: {
    flex: 1,
    textAlign: "center",
    fontSize: 24,
    color: "#4d2c1d",
    fontFamily: "AbhayaLibre_700Bold",
    paddingHorizontal: 6,
  },
  resultsHeaderSpacer: {
    width: 44,
    height: 44,
  },
  resultsHeaderHelpRow: {
    alignItems: "center",
    justifyContent: "center",
    paddingBottom: 2,
  },
  resultsHelpButton: {
    width: 30,
    height: 30,
    alignItems: "center",
    justifyContent: "center",
  },
  resultsContent: {
    flex: 1,
    padding: 16,
  },
  resultsSection: {
    flex: 1,
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  loadingCard: {
    width: "100%",
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "#E5D9D2",
    backgroundColor: "#FFFDFB",
    paddingVertical: 24,
    paddingHorizontal: 18,
    alignItems: "center",
  },
  loadingTitle: {
    fontSize: 20,
    color: "#4d2c1d",
    fontFamily: "AbhayaLibre_700Bold",
  },
  loadingSubtitle: {
    marginTop: 4,
    fontSize: 15,
    color: "#9C8A80",
    fontFamily: "AbhayaLibre_400Regular",
    textAlign: "center",
  },
  errorContainer: {
    alignItems: "center",
    paddingVertical: 28,
    paddingHorizontal: 24,
    minWidth: 280,
  },
  errorTitle: {
    fontSize: 22,
    color: "#4d2c1d",
    fontFamily: "AbhayaLibre_700Bold",
    textAlign: "center",
  },
  errorMessage: {
    marginTop: 12,
    fontSize: 17,
    color: "#9C8A80",
    fontFamily: "AbhayaLibre_400Regular",
    textAlign: "center",
    maxWidth: 300,
    lineHeight: 24,
  },
  resultCard: {
    width: "100%",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E5D9D2",
    backgroundColor: "#FFFDFB",
    paddingVertical: 12,
    paddingHorizontal: 14,
    shadowColor: "#4d2c1d",
    shadowOpacity: 0.06,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 3,
  },
  resultTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  resultBadgeRow: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 0,
    gap: 6,
  },
  resultBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    borderRadius: 999,
    paddingVertical: 3,
    paddingHorizontal: 8,
    backgroundColor: "#F3E8E2",
  },
  resultBadgeText: {
    color: "#6B4C3B",
    fontSize: 11,
    fontFamily: "AbhayaLibre_700Bold",
  },
  distancePill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    borderRadius: 999,
    paddingVertical: 3,
    paddingHorizontal: 8,
    backgroundColor: "#F3E8E2",
  },
  distancePillText: {
    color: "#7D695F",
    fontSize: 11,
    fontFamily: "AbhayaLibre_700Bold",
  },
  resultTitle: {
    flex: 1,
    color: "#4d2c1d",
    fontSize: 20,
    lineHeight: 24,
    fontFamily: "AbhayaLibre_700Bold",
  },
  resultDescription: {
    marginTop: 4,
    color: "#6B4C3B",
    fontSize: 14,
    lineHeight: 19,
    fontFamily: "AbhayaLibre_400Regular",
  },
  addressRow: {
    marginTop: 6,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  addressText: {
    flex: 1,
    color: "#7D695F",
    fontSize: 13,
    lineHeight: 18,
    fontFamily: "AbhayaLibre_400Regular",
  },
  addressActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  directionsButton: {
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: 999,
    backgroundColor: "#6B4C3B",
  },
  bottomActions: {
    paddingHorizontal: 24,
    paddingBottom: 24,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
    backgroundColor: "#F9F5F3",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  retryButton: {
    paddingVertical: 14,
    paddingHorizontal: 28,
    borderRadius: 999,
    backgroundColor: "#6B4C3B",
  },
  retryButtonText: {
    fontSize: 18,
    color: "#FFFFFF",
    fontFamily: "AbhayaLibre_700Bold",
  },
  closeBodyButton: {
    width: 220,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    borderRadius: 999,
    borderWidth: 2,
    borderColor: "#6B4C3B",
    backgroundColor: "#FFFFFF",
    paddingVertical: 12,
  },
  closeBodyButtonText: {
    color: "#4d2c1d",
    fontSize: 16,
    fontFamily: "AbhayaLibre_700Bold",
  },
});

export default HousePickBottomSheet;
